/*
 * claude-ai-shopper.js — The Heartbeat Brand AI shopping assistant.
 *
 * Built at 390px first. The desktop layout is a min-width query at the bottom
 * of the stylesheet; nothing here assumes a mouse or a wide viewport.
 *
 * Talks to the Heartbeat shopper service over SSE. Adds to cart through
 * Shopify's own /cart/add.js on the storefront origin, so there is no token in
 * the browser and the theme's real cart is the one that updates.
 */
(function () {
  'use strict'

  var CFG = window.HeartbeatShopper || {}
  var ENDPOINT = String(CFG.endpoint || '').replace(/\/$/, '')
  if (!ENDPOINT) return // snippet not configured yet — stay invisible

  var GREETING = CFG.greeting ||
    'Good to meet you! You can call me The Hat Bot! Let me know if you have anything specific in mind or if you want me to help guide you in the right direction.'
  var STORAGE_KEY = 'hb-shopper-thread'
  var MAX_TURNS = 20

  var els = {}
  var thread = []      // [{role, content}] plain text only
  var busy = false
  var opened = false

  // ------------------------------------------------------------ utilities

  function el (tag, cls, text) {
    var n = document.createElement(tag)
    if (cls) n.className = cls
    if (text != null) n.textContent = text
    return n
  }

  function money (amount, currency) {
    if (amount == null) return ''
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2
      }).format(amount)
    } catch (e) {
      return '$' + Number(amount).toFixed(2)
    }
  }

  function loadThread () {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY)
      if (raw) thread = JSON.parse(raw) || []
    } catch (e) { thread = [] }
  }

  function saveThread () {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(thread.slice(-MAX_TURNS)))
    } catch (e) { /* private mode — the thread just won't survive a reload */ }
  }

  /* Stable per-session conversation id, so the server can store the thread
     for review. No account, no cookie — dies with the tab session. */
  function convId () {
    try {
      var id = sessionStorage.getItem('hbs-conv-id')
      if (!id) {
        id = 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
        sessionStorage.setItem('hbs-conv-id', id)
      }
      return id
    } catch (e) { return 'anon' + Date.now().toString(36) }
  }

  function scrollToEnd () {
    // rAF so it runs after layout; otherwise the new node isn't measured yet.
    requestAnimationFrame(function () {
      els.log.scrollTop = els.log.scrollHeight
    })
  }

  // ------------------------------------------------------------ rendering

  function addBubble (role, text) {
    var row = el('div', 'hbs-row hbs-row--' + role)
    var bubble = el('div', 'hbs-bubble hbs-bubble--' + role, text || '')
    row.appendChild(bubble)
    els.log.appendChild(row)
    scrollToEnd()
    return bubble
  }

  function addStatus (text) {
    var existing = els.log.querySelector('.hbs-status')
    if (existing) existing.remove()
    var n = el('div', 'hbs-status')
    n.appendChild(el('span', 'hbs-status__dot'))
    n.appendChild(el('span', null, text))
    els.log.appendChild(n)
    scrollToEnd()
    return n
  }

  function clearStatus () {
    var n = els.log.querySelector('.hbs-status')
    if (n) n.remove()
  }

  function renderProducts (products) {
    var wrap = el('div', 'hbs-cards')
    products.forEach(function (p) { wrap.appendChild(productCard(p)) })
    els.log.appendChild(wrap)
    productsThisTurn = true
    // Anchor the view at the assistant's message, not the bottom of the cards:
    // the shopper reads the message first and scrolls down through the hats
    // themselves. A ~90px peek of the first card signals there's more below.
    requestAnimationFrame(function () {
      var target = wrap.offsetTop - els.log.clientHeight + 90
      els.log.scrollTop = Math.max(0, Math.min(target, els.log.scrollHeight))
    })
  }

  /*
   * Collection cards: the collection's name over a representative photo. The
   * whole card is a button — tapping it answers the bot with the collection
   * name, exactly as if the shopper tapped that name's chip. It never
   * navigates away from the conversation.
   */
  function renderCollections (collections) {
    var wrap = el('div', 'hbs-cards hbs-colcards')
    collections.forEach(function (c) {
      var card = el('button', 'hbs-colcard')
      card.type = 'button'
      card.setAttribute('aria-label', 'Choose ' + c.title)
      if (c.image) {
        var img = el('img', 'hbs-colcard__img')
        img.src = c.image + (c.image.indexOf('?') > -1 ? '&' : '?') + 'width=400'
        img.alt = c.title
        img.loading = 'lazy'
        img.decoding = 'async'
        card.appendChild(img)
      }
      card.appendChild(el('span', 'hbs-colcard__name', c.title))
      card.addEventListener('click', function () {
        send(c.title)
      })
      wrap.appendChild(card)
    })
    els.log.appendChild(wrap)
    productsThisTurn = true
    requestAnimationFrame(function () {
      var target = wrap.offsetTop - els.log.clientHeight + 90
      els.log.scrollTop = Math.max(0, Math.min(target, els.log.scrollHeight))
    })
  }

  function productCard (p) {
    var card = el('article', 'hbs-card')

    var link = el('a', 'hbs-card__media')
    link.href = p.url
    link.setAttribute('aria-label', p.title)
    if (p.image) {
      var img = el('img', 'hbs-card__img')
      // Shopify CDN sizing: ask for what we actually render, not the original.
      img.src = p.image + (p.image.indexOf('?') > -1 ? '&' : '?') + 'width=400'
      img.alt = p.imageAlt || p.title
      img.loading = 'lazy'
      img.decoding = 'async'
      link.appendChild(img)
    }
    card.appendChild(link)

    var body = el('div', 'hbs-card__body')

    var title = el('a', 'hbs-card__title', p.title)
    title.href = p.url
    body.appendChild(title)

    var priceText = money(p.price, p.currency)
    if (p.priceMax != null && p.priceMax !== p.price) {
      priceText = 'From ' + priceText
    }
    body.appendChild(el('div', 'hbs-card__price', priceText))

    var variants = p.variants || []
    var select = null

    if (variants.length > 1) {
      select = el('select', 'hbs-card__variant')
      select.setAttribute('aria-label', 'Choose an option for ' + p.title)
      variants.forEach(function (v) {
        var opt = el('option', null, v.options || v.title)
        opt.value = v.id
        select.appendChild(opt)
      })
      body.appendChild(select)
    }

    var btn = el('button', 'hbs-card__add', variants.length ? 'Add to cart' : 'Sold out')
    btn.type = 'button'
    if (!variants.length) {
      btn.disabled = true
    } else {
      btn.addEventListener('click', function () {
        var id = select ? select.value : variants[0].id
        addToCart(id, btn)
      })
    }
    body.appendChild(btn)

    card.appendChild(body)
    return card
  }

  // ------------------------------------------------------------ cart

  function addToCart (variantId, btn) {
    var original = btn.textContent
    btn.disabled = true
    btn.textContent = 'Adding…'

    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add that.') })
        return r.json()
      })
      .then(function () {
        btn.textContent = 'Added ✓'
        btn.classList.add('is-added')
        notifyTheme()
        setTimeout(function () {
          btn.textContent = original
          btn.classList.remove('is-added')
          btn.disabled = false
        }, 2600)
      })
      .catch(function (err) {
        btn.textContent = 'Try again'
        btn.disabled = false
        addBubble('assistant', err.message || 'That one wouldn’t add — it may have just sold out.')
        setTimeout(function () { btn.textContent = original }, 2600)
      })
  }

  /*
   * Nudge the theme to refresh its cart count. Themes disagree on how to be
   * told, so fire the common signals and let whichever one the theme listens
   * for win. If none of them land, the count updates on next page load — the
   * item is in the cart either way.
   */
  function notifyTheme () {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
    document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }))
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { source: 'hb-shopper' } })
    )
    if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function') {
      try { window.Shopify.onCartUpdate() } catch (e) {}
    }
  }

  // ------------------------------------------------------------ streaming

  var pendingChoices = null
  var pendingCollectionLink = null
  var productsThisTurn = false
  var headerMode = false

  function send (text) {
    if (busy || !text.trim()) return
    busy = true
    // Earlier choice buttons stay in the log on purpose (design ruling 47):
    // a shopper can scroll back and reselect a different preference at any
    // point, and each tap just sends that answer as a new message.
    pendingChoices = null
    pendingCollectionLink = null
    productsThisTurn = false
    els.send.disabled = true
    els.input.value = ''
    els.input.style.height = 'auto'

    addBubble('user', text)
    thread.push({ role: 'user', content: text })
    saveThread()

    addStatus('Thinking')

    var bubble = null
    var acc = ''

    fetch(ENDPOINT + '/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: thread.slice(-MAX_TURNS), conversation_id: convId() })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return {} }).then(function (j) {
            throw new Error(j.error || 'The assistant is unavailable right now.')
          })
        }
        return readStream(res.body, function (event, data) {
          if (event === 'status') {
            addStatus(data.text)
          } else if (event === 'text') {
            clearStatus()
            if (!bubble) bubble = addBubble('assistant', '')
            acc += data.text
            bubble.textContent = acc
            if (!productsThisTurn) scrollToEnd()
          } else if (event === 'products') {
            clearStatus()
            renderProducts(data.products || [])
          } else if (event === 'collections') {
            clearStatus()
            renderCollections(data.collections || [])
          } else if (event === 'choices') {
            pendingChoices = data.options || null
          } else if (event === 'collection_link') {
            pendingCollectionLink = data
          } else if (event === 'error') {
            clearStatus()
            addBubble('assistant', data.message)
          }
        })
      })
      .then(function () {
        clearStatus()
        if (acc.trim()) {
          thread.push({ role: 'assistant', content: acc })
          saveThread()
        }
        if (pendingChoices && pendingChoices.length) {
          renderChoices(pendingChoices)
          pendingChoices = null
        }
        if (pendingCollectionLink) {
          renderCollectionLink(pendingCollectionLink)
          pendingCollectionLink = null
        }
      })
      .catch(function (err) {
        clearStatus()
        addBubble('assistant', err.message || 'Something went wrong. Try again in a moment.')
      })
      .finally(function () {
        busy = false
        els.send.disabled = false
        els.input.focus()
      })
  }

  /* Minimal SSE parser over a fetch body stream. */
  function readStream (body, onEvent) {
    var reader = body.getReader()
    var decoder = new TextDecoder()
    var buffer = ''

    function pump () {
      return reader.read().then(function (result) {
        if (result.done) return
        buffer += decoder.decode(result.value, { stream: true })

        var parts = buffer.split('\n\n')
        buffer = parts.pop() // last chunk may be incomplete

        parts.forEach(function (chunk) {
          var event = 'message'
          var dataLines = []
          chunk.split('\n').forEach(function (line) {
            if (line.indexOf('event:') === 0) event = line.slice(6).trim()
            else if (line.indexOf('data:') === 0) dataLines.push(line.slice(5).trim())
            // lines starting with ':' are keepalive comments — ignored
          })
          if (!dataLines.length) return
          try {
            onEvent(event, JSON.parse(dataLines.join('\n')))
          } catch (e) { /* malformed frame — skip it rather than kill the stream */ }
        })

        return pump()
      })
    }
    return pump()
  }

  // ------------------------------------------------------------ chrome

  var CHAT_ICON =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M12 3c-4.97 0-9 3.58-9 8 0 2.4 1.2 4.55 3.1 6.02L5.5 21l3.9-2.05c.83.2 1.7.3 2.6.3 4.97 0 9-3.58 9-8s-4.03-8.25-9-8.25z" ' +
    'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>'

  function build () {
    // Header mode: when the theme provides a mount (the old search-bar slot),
    // the assistant lives IN the header on every page and the chat drops down
    // from it. Without a mount we fall back to the floating corner button.
    var mount = document.querySelector('[data-heartbeat-shopper-mount]') ||
      (CFG.mount ? document.querySelector(CFG.mount) : null)
    headerMode = Boolean(mount)

    var launcher
    if (headerMode) {
      launcher = el('button', 'hbs-bar')
      launcher.type = 'button'
      launcher.setAttribute('aria-label', 'Open the AI shopping assistant')
      launcher.innerHTML = CHAT_ICON + '<span>' + (CFG.barText || 'Chat with your AI Personal Shopper') + '</span>'
      launcher.addEventListener('click', function () {
        if (els.panel.hidden) open()
        else hide()
      })
      mount.appendChild(launcher)
    } else {
      launcher = el('button', 'hbs-launcher')
      launcher.type = 'button'
      launcher.setAttribute('aria-label', 'Open the AI shopping assistant')
      launcher.innerHTML = CHAT_ICON + '<span>AI Shopping Assistant</span>'
      launcher.addEventListener('click', open)
      document.body.appendChild(launcher)
    }
    els.launcher = launcher
    els.mount = mount

    var panel = el('div', 'hbs-panel' + (headerMode ? ' hbs-panel--header' : ''))
    panel.setAttribute('role', 'dialog')
    panel.setAttribute('aria-modal', 'true')
    panel.setAttribute('aria-label', 'Shopping assistant')
    panel.hidden = true

    var head = el('header', 'hbs-head')
    var heading = el('h2', 'hbs-head__title', CFG.title || 'Find Your Perfect Hat')
    head.appendChild(heading)

    var close = el('button', 'hbs-head__close')
    close.type = 'button'
    close.setAttribute('aria-label', headerMode ? 'Minimize the assistant' : 'Close the shopping assistant')
    close.innerHTML = headerMode
      ? '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M5 14l7-6 7 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>'
    close.addEventListener('click', hide)
    head.appendChild(close)
    panel.appendChild(head)

    els.log = el('div', 'hbs-log')
    els.log.setAttribute('role', 'log')
    els.log.setAttribute('aria-live', 'polite')
    panel.appendChild(els.log)

    var form = el('form', 'hbs-form')
    els.input = el('textarea', 'hbs-input')
    els.input.rows = 1
    els.input.placeholder = 'What are you looking for?'
    els.input.setAttribute('aria-label', 'Message the shopping assistant')
    els.input.addEventListener('input', function () {
      this.style.height = 'auto'
      this.style.height = Math.min(this.scrollHeight, 120) + 'px'
    })
    els.input.addEventListener('keydown', function (e) {
      // Enter sends on desktop; on a phone the on-screen return key inserts a
      // newline as expected, because there's no physical Enter to intercept.
      if (e.key === 'Enter' && !e.shiftKey && window.matchMedia('(min-width: 750px)').matches) {
        e.preventDefault()
        send(els.input.value)
      }
    })
    form.appendChild(els.input)

    els.send = el('button', 'hbs-send')
    els.send.type = 'submit'
    els.send.setAttribute('aria-label', 'Send')
    els.send.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M4 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>'
    form.appendChild(els.send)

    form.addEventListener('submit', function (e) {
      e.preventDefault()
      send(els.input.value)
    })
    panel.appendChild(form)

    var foot = el('p', 'hbs-foot', 'AI assistant · double-check sizing before you buy')
    panel.appendChild(foot)

    // "More below" arrow: floats over the bottom of the log whenever there is
    // unseen content beneath the fold; a tap scrolls to the newest message.
    els.more = el('button', 'hbs-more')
    els.more.type = 'button'
    els.more.setAttribute('aria-label', 'Scroll to see more')
    els.more.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M5 10l7 6 7-6" fill="none" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>'
    els.more.addEventListener('click', function () {
      els.log.scrollTo({ top: els.log.scrollHeight, behavior: 'smooth' })
    })
    panel.appendChild(els.more)

    // "Start over" (design ruling 49): wipes the thread and boots a brand-new
    // conversation — new greeting, new suggestion chips, new conversation id.
    els.restart = el('button', 'hbs-restart')
    els.restart.type = 'button'
    els.restart.setAttribute('aria-label', 'Start the conversation over')
    els.restart.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M4 10a8 8 0 1 1 2 6M4 10V5m0 5h5" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '<span>Start over</span>'
    els.restart.addEventListener('click', startOver)
    panel.appendChild(els.restart)

    els.log.addEventListener('scroll', updateMoreArrow, { passive: true })
    // Anything appended to the log (rows, cards, chips) can push content below
    // the fold without a scroll event — re-check on every DOM change, and on
    // image loads, which grow scrollHeight after the cards are appended.
    new MutationObserver(updateMoreArrow).observe(els.log, { childList: true, subtree: true })
    els.log.addEventListener('load', updateMoreArrow, true)
    window.addEventListener('resize', updateMoreArrow)

    document.body.appendChild(panel)
    els.panel = panel

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) hide()
    })
    window.addEventListener('resize', positionPanel)
    window.addEventListener('scroll', positionPanel, { passive: true })
  }

  function updateMoreArrow () {
    if (!els.more || !els.log || els.panel.hidden) return
    var below = els.log.scrollHeight - els.log.scrollTop - els.log.clientHeight
    els.more.classList.toggle('is-visible', below > 40)
    // Pin the arrow just above the bottom edge of the log, whatever height the
    // input row happens to be (the textarea grows as you type).
    var gap = els.panel.clientHeight - (els.log.offsetTop + els.log.offsetHeight)
    els.more.style.bottom = (gap + 10) + 'px'
    if (els.restart) els.restart.style.bottom = (gap + 10) + 'px'
  }

  /* Wipe everything and begin as if the shopper had never chatted: empty log,
     fresh greeting and suggestions, and a NEW conversation id so the server
     records it as a separate conversation. */
  function startOver () {
    if (busy) return // mid-stream reset would interleave events into a cleared log
    thread = []
    try {
      sessionStorage.removeItem(STORAGE_KEY)
      sessionStorage.removeItem('hbs-conv-id')
    } catch (e) { /* private mode — in-memory reset still happens */ }
    els.log.innerHTML = ''
    addBubble('assistant', GREETING)
    renderSuggestions()
    updateMoreArrow()
    els.input.focus()
  }

  function positionPanel () {
    if (!headerMode || !els.mount || els.panel.hidden) return
    var r = els.mount.getBoundingClientRect()
    var top = Math.max(0, r.bottom + 8)
    els.panel.style.top = top + 'px'
    els.panel.style.maxHeight = (window.innerHeight - top - 12) + 'px'
    if (window.matchMedia('(min-width: 750px)').matches) {
      // Desktop: drop down aligned with the bar itself
      els.panel.style.left = r.left + 'px'
      els.panel.style.width = r.width + 'px'
      els.panel.style.right = 'auto'
    } else {
      els.panel.style.left = ''
      els.panel.style.width = ''
      els.panel.style.right = ''
    }
  }

  function open () {
    els.panel.hidden = false
    document.body.classList.add('hbs-open')
    positionPanel()
    els.launcher.setAttribute('aria-expanded', 'true')

    if (!opened) {
      opened = true
      if (thread.length) {
        thread.forEach(function (m) { addBubble(m.role, m.content) })
      } else {
        addBubble('assistant', GREETING)
        renderSuggestions()
      }
    }
    setTimeout(function () { els.input.focus() }, 50)
    setTimeout(updateMoreArrow, 60)
  }

  function hide () {
    // Minimize, not destroy: the thread stays in memory and sessionStorage,
    // so reopening picks the conversation up exactly where it was.
    els.panel.hidden = true
    document.body.classList.remove('hbs-open')
    els.launcher.setAttribute('aria-expanded', 'false')
    if (!headerMode) els.launcher.focus()
  }

  /* "View the full collection" bar under a set of product cards. */
  function renderCollectionLink (link) {
    if (!link || !link.url) return
    var name = String(link.title || 'the collection').replace(/\s+hats?$/i, '')
    // A custom label (e.g. "Open the Custom tab \u2192") is used verbatim.
    var text = link.label || ('Shop the full ' + name + ' collection \u2192')
    var a = el('a', 'hbs-collection-link', text)
    a.href = link.url
    a.target = '_blank'
    a.rel = 'noopener'
    els.log.appendChild(a)
    if (!link.label) {
      // The lateral escape (design ruling 42): once they're deep in one
      // collection, one tap under the bar opens the rest of the catalog.
      var wrap = el('div', 'hbs-chips hbs-choices')
      var chip = el('button', 'hbs-chip', 'Browse other collections')
      chip.type = 'button'
      chip.addEventListener('click', function () {
        send('Browse other collections')
      })
      wrap.appendChild(chip)
      els.log.appendChild(wrap)
    }
    if (!productsThisTurn) scrollToEnd()
  }

  /* Tappable answers to the assistant's current question (offer_choices). */
  function renderChoices (options) {
    var wrap = el('div', 'hbs-chips hbs-choices')
    options.forEach(function (text) {
      var chip = el('button', 'hbs-chip', text)
      chip.type = 'button'
      chip.addEventListener('click', function () {
        send(text)
      })
      wrap.appendChild(chip)
    })
    els.log.appendChild(wrap)
    if (!productsThisTurn) scrollToEnd()
  }

  function renderSuggestions () {
    var picks = CFG.suggestions || [
      'Something for a trip',
      'Looking for a gift',
      'Guide me based on my fit and interests'
    ]
    var wrap = el('div', 'hbs-chips')
    picks.forEach(function (text) {
      var chip = el('button', 'hbs-chip', text)
      chip.type = 'button'
      chip.addEventListener('click', function () {
        wrap.remove()
        send(text)
      })
      wrap.appendChild(chip)
    })
    els.log.appendChild(wrap)
    scrollToEnd()
  }

  // ------------------------------------------------------------ boot

  function init () {
    loadThread()
    build()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
